import {
  Container,
  Stack,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  CardContent,
  Snackbar,
  Alert,
  TextField,
  CircularProgress,
} from '@mui/material';
import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'src/routes/hooks';
import { LoadingScreen } from 'src/components/loading-screen';
import { getUserTelePackageResponse, updateUserTelePackageResponse, generatePrescription, getPrescription,deletePrescription } from 'src/services/telehealth-packages.service';
import { RHFTextField } from 'src/components/hook-form';
import { getProduct } from 'src/services/products.service';
import { getBulkSecureViewUrls, getSecureViewUrl } from 'src/utils/secure-files';
import { useAuthContext } from '../../../auth/hooks';

export default function UserTelePackageResponseView() {
  const { user } = useAuthContext();
  const { userResponseId } = useParams();
  const [responseDetails, setResponseDetails] = useState(null);
  const [products, setProducts] = useState([]);


  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isGenerating, setIsGenerating] = useState(false);
  const [prescriptionGeneratedPdf, setPrescriptionGeneratedPdf] = useState(null);
  const [imageUrls, setImageUrls] = useState({});

  useEffect(() => {
    if (!responseDetails) return;
    
    const fetchUrls = async () => {
      const allImages = [];
      responseDetails.telecare_package_id?.questions_id?.questions?.forEach((q) => {
        if (q.questionType === 'image-upload') {
          const answer = responseDetails.content?.find((c) => c.question_number === q.questionNumber);
          if (answer?.response) {
            if (Array.isArray(answer.response)) {
              allImages.push(...answer.response);
            } else {
              allImages.push(answer.response);
            }
          }
        }
      });
      
      if (allImages.length > 0) {
        const mappedUrls = await getBulkSecureViewUrls(allImages);
        setImageUrls(mappedUrls);
      }
    };
    fetchUrls();
  }, [responseDetails]);

  const schema = Yup.object().shape({
    doctor_notes: Yup.string().nullable(),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      doctor_notes: '',
    },
  });

  const { setValue, watch, getValues } = methods;

  useEffect(() => {

    getUserTelePackageResponse(userResponseId)
      .then((res) => {
        setResponseDetails(res);
        setValue('doctor_notes', res.doctor_notes || '');
      })
      .catch(console.log);
  }, [userResponseId, setValue]);

  useEffect(() => {
    if (
      responseDetails?.status === 'prescription_generated' ||
      responseDetails?.status === 'approved'
    ) {
      getPrescription(userResponseId)
        .then(async (res) => {
          if (res?.pdf_url) {
            const url = await getSecureViewUrl(res.pdf_url);
            setPrescriptionGeneratedPdf(url || res.pdf_url);
          }
        })
        .catch(console.log);
    }
  }, [responseDetails?.status, userResponseId]);

  useEffect(() => {
    if (!responseDetails?.telecare_package_id || products.length) return;

    const productIds = [
      ...new Set(
        responseDetails.telecare_package_id.therapy_ids.flatMap(
          (t) => t.products?.map((p) => p.id) || []
        )
      )
    ];

    Promise.all(
      productIds.map((id) =>
        getProduct(id, process.env.REACT_APP_TELE_CLINIC_ID)
      )
    )
      .then(setProducts)
      .catch(console.log);

  }, [responseDetails?.telecare_package_id, products.length]);

  const hasPrescriptionRequiredProduct = () =>
    responseDetails.products_array?.some((pa) => {
      const prod = products.find((p) => p.id === pa.product_id);
      return prod?.prescription_required === true;
    });

  const hasAtLeastOneProduct = () => responseDetails.products_array && responseDetails.products_array.length > 0;

  const handleGeneratePrescription = () => {
    if (responseDetails.status === 'rejected') return;
    if (!getValues('doctor_notes') || getValues('doctor_notes').trim() === '') {
      setSnackbarMessage('Doctor notes are required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!hasAtLeastOneProduct()) {
      setSnackbarMessage('Please add at least one product');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    const invalid = responseDetails.products_array?.some(
      p =>
        !p.amount ||
        !p.interval ||
        !p.number_of_units ||
        !p.timing ||
        (!p.timing.morning && !p.timing.afternoon && !p.timing.night)
    );

    if (invalid) {
      setSnackbarMessage('Fill all product details');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    setIsGenerating(true);
    generatePrescription(userResponseId, {
      products: responseDetails.products_array.map(p => ({
        product_id: p.product_id,
        timing: p.timing,
        amount: p.amount,
        interval: p.interval,
        number_of_units: p.number_of_units
      })),
      doctor_notes: getValues('doctor_notes')
    })
      .then(async (res) => {
        setIsGenerating(false);
        setSnackbarMessage('Prescription generated successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        if (res.pdf_url) {
          const url = await getSecureViewUrl(res.pdf_url);
          setPrescriptionGeneratedPdf(url || res.pdf_url);
        }
      })
      .then((res) => {
        if (res) setResponseDetails(res);
      })
      .catch((err) => {
        setIsGenerating(false);
        setSnackbarMessage(`Error: ${err.message}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };
  const handleDeletePrescription = () => {
    deletePrescription(userResponseId)
      .then(() => {
        setPrescriptionGeneratedPdf(null);
        setResponseDetails((prev) => ({
          ...prev,
          status: 'under_review',
        }));
        setSnackbarMessage('Prescription deleted successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      })
      .catch((err) => {
        setSnackbarMessage(`Error: ${err.message}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };
  const handleStatusUpdate = (status) => {
    if (!getValues('doctor_notes') || getValues('doctor_notes').trim() === '') {
      setSnackbarMessage('Doctor notes are required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (status !== 'rejected') {
      if (!hasAtLeastOneProduct()) {
        setSnackbarMessage('Please add at least one product');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      const invalid = responseDetails.products_array?.some(
        p =>
          !p.amount ||
          !p.interval ||
          !p.timing ||
          (!p.timing.morning && !p.timing.afternoon && !p.timing.night)
      );

      if (invalid) {
        setSnackbarMessage('Fill all product details');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
    }

    updateUserTelePackageResponse(userResponseId, {
      doctor_notes: getValues('doctor_notes'),
      status,
      products_array:
        status === 'rejected'
          ? []
          : responseDetails.products_array.map(p => ({
            product_id: p.product_id,
            timing: p.timing,
            amount: p.amount,
            interval: p.interval,
            number_of_units: p.number_of_units
          })),
      doctor_id: user.doctor_id,
    })
      .then((res) => {
        setResponseDetails(res);
        setSnackbarMessage(`Response ${status} successfully`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      })
      .catch((err) => {
        setSnackbarMessage(`Error: ${err.message}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };


  if (!responseDetails) return <LoadingScreen />;
  const isRejected = responseDetails.status === 'rejected';
  const hasProducts = hasAtLeastOneProduct();
  const disableApprove = responseDetails.status === 'approved';
  const showApproveBtn =
    hasProducts &&
    (responseDetails.status === 'prescription_generated' || prescriptionGeneratedPdf);
  const disable = (responseDetails.status === 'prescription_generated' || prescriptionGeneratedPdf);

  const showGenerateBtn =
    hasProducts &&
    !showApproveBtn &&
    responseDetails.status !== 'approved' &&
    responseDetails.status !== 'rejected';
  const disableReject = responseDetails.status === 'approved' || responseDetails.status === 'rejected';

  return (
    <Container>
      <Typography variant="h4" mb={3}>User Telepackage Response</Typography>

      <FormProvider {...methods}>
        <Stack gap={3}>
          <Stack>
            <Typography variant="h6">User</Typography>
            <Typography>{responseDetails.user_id.username}</Typography>
          </Stack>

          <Stack>
            <Typography variant="h6">Telecare Package</Typography>
            <Typography>{responseDetails.telecare_package_id.package_name}</Typography>
          </Stack>

          <Stack>
            <Typography variant="h6">Status</Typography>
            <Typography>{responseDetails.status}</Typography>
          </Stack>

          <Stack>
            <Typography variant="h6">Consultation Fee</Typography>
            <Typography>{responseDetails.consultation_fee ?? '-'}</Typography>
          </Stack>

          <Stack>
            <Typography variant="h6">Responses</Typography>

            {responseDetails.telecare_package_id.questions_id.questions.map((q) => {
              const answer = responseDetails.content.find(
                (c) => c.question_number === q.questionNumber
              );

              let renderedValue = <Typography>-</Typography>;

              if (q.questionType === 'image-upload') {
                let images = [];

                if (Array.isArray(answer?.response)) {
                  images = answer.response;
                } else if (answer?.response) {
                  images = [answer.response];
                }

                if (images.length) {
                  renderedValue = (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {images.map((url, idx) => (
                        <img
                          key={idx}
                          src={imageUrls[url] || url}
                          alt={`Upload-${idx}`}
                          style={{
                            maxWidth: 450,
                            maxHeight: 450,
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            borderRadius: 8,
                          }}
                        />
                      ))}
                    </Stack>
                  );
                }
              } else if (Array.isArray(answer?.response)) {
                renderedValue = <Typography>{answer.response.join(', ')}</Typography>;
              } else if (answer?.response) {
                renderedValue = <Typography>{answer.response}</Typography>;
              }

              return (
                <Stack key={q.questionNumber} mb={1} spacing={1}>
                  <Typography variant="subtitle2">{q.questionText}</Typography>
                  {renderedValue}
                </Stack>
              );
            })}
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RHFTextField name="doctor_notes" label="Doctor Notes" multiline rows={4} fullWidth />
            </Grid>
          </Grid>



          <Stack direction="row" gap={2} justifyContent="flex-end">
            {prescriptionGeneratedPdf && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => window.open(prescriptionGeneratedPdf, '_blank')}
                >
                  View Generated Prescription (PDF)
                </Button>

                {responseDetails.status !== 'approved' && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeletePrescription}
                  >
                    Delete Prescription
                  </Button>
                )}
              </>
            )}

            {showGenerateBtn && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleGeneratePrescription}
                disabled={isGenerating}
                startIcon={isGenerating ? <CircularProgress size={20} /> : null}
              >
                {isGenerating ? 'Generating...' : 'Generate Prescription'}
              </Button>
            )}

            {showApproveBtn && (
              <Button
                variant="contained"
                color="success"
                disabled={disableApprove}
                onClick={() => handleStatusUpdate('approved')}
              >
                Approve
              </Button>
            )}

            <Button
              variant="contained"
              color="error"
              disabled={disableReject}
              onClick={() => handleStatusUpdate('rejected')}
            >
              Reject
            </Button>
          </Stack>

        </Stack>
      </FormProvider>
      <Typography variant="h6" mb={2}>Listed Products</Typography>
      <Typography variant="body2" sx={{ color: 'error.main' }} mb={2}>
        Please calculate exact units for 1 month<br />
        Eg: (dose×timings×interval) ≤ units×quantity<br />
        Use correct dosage metric
        Eg: ml for ml,tablets for tablets, spoon for syrup etc.
      </Typography>
      <Grid container spacing={2}>
        {products.map((p) => {
          const existing = responseDetails.products_array?.find(
            (pa) => pa.product_id === p.id
          );

          const isAdded = !!existing;
          const timing = existing?.timing || '';
          const amount = existing?.amount || '';
          const interval = existing?.interval || 1;
          const numberOfUnits = existing?.number_of_units || 1;

          return (
            <Grid item xs={12} md={4} key={p.id}>
              <Card
                sx={{
                  border: isAdded ? '2px solid green' : '1px solid #e0e0e0',
                  opacity: isAdded ? 1 : 0.9,
                  p: 1,
                }}
              >
                <CardMedia component="img" height="140" image={p.image_url} />

                <CardContent>
                  <Typography fontWeight={600}>{p.product_name}</Typography>
                  <Typography variant="body2">Each unit contains {p.quantity}</Typography>


                  {p.prescription_required && (
                    <Typography color="error" variant="caption">
                      Prescription Required
                    </Typography>
                  )}

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mt={1}
                  >
                    <Button
                      size="small"
                      variant={isAdded ? 'outlined' : 'contained'}
                      color={isAdded ? 'error' : 'primary'}
                      disabled={isRejected || disable}
                      onClick={() => {
                        if (isAdded) {
                          const updated = responseDetails.products_array.filter(
                            (pa) => pa.product_id !== p.id
                          );
                          setResponseDetails((prev) => ({
                            ...prev,
                            products_array: updated,
                          }));
                        } else {
                          setResponseDetails((prev) => ({
                            ...prev,
                            products_array: [
                              ...(prev.products_array || []),
                              {
                                product_id: p.id,
                                timing: {
                                  morning: false,
                                  afternoon: false,
                                  night: false,
                                },
                                amount: '',
                                interval: 1,
                                number_of_units: 1,
                              },
                            ],
                          }));
                        }
                      }}
                    >
                      {isAdded ? 'Remove' : 'Add'}
                    </Button>
                  </Stack>

                  {isAdded && !isRejected && (
                    <Stack spacing={1} mt={2}>
                      <Stack direction="row" spacing={1}>
                        {['morning', 'afternoon', 'night'].map((t) => (
                          <Button
                            key={t}
                            size="small"
                            variant={timing?.[t] ? 'contained' : 'outlined'}
                            onClick={() => {
                              const updated = responseDetails.products_array.map((pa) =>
                                pa.product_id === p.id
                                  ? {
                                    ...pa,
                                    timing: {
                                      ...pa.timing,
                                      [t]: !pa.timing?.[t],
                                    },
                                  }
                                  : pa
                              );

                              setResponseDetails((prev) => ({
                                ...prev,
                                products_array: updated,
                              }));
                            }}
                          >
                            {t}
                          </Button>
                        ))}
                      </Stack>

                      <TextField
                        size="small"
                        label="Dose (e.g. 1ml / 1 spoon)"
                        value={amount}
                        onChange={(e) => {
                          const updated = responseDetails.products_array.map((pa) =>
                            pa.product_id === p.id
                              ? { ...pa, amount: e.target.value }
                              : pa
                          );

                          setResponseDetails((prev) => ({
                            ...prev,
                            products_array: updated,
                          }));
                        }}
                      />

                      <TextField
                        type="number"
                        size="small"
                        label="Interval"
                        value={interval}
                        onChange={(e) => {
                          const value = Math.max(1, parseInt(e.target.value, 10) || 1);

                          const updated = responseDetails.products_array.map((pa) =>
                            pa.product_id === p.id
                              ? { ...pa, interval: value }
                              : pa
                          );

                          setResponseDetails((prev) => ({
                            ...prev,
                            products_array: updated,
                          }));
                        }}
                      />
                      <TextField
                        type="number"
                        size="small"
                        label="Number of Units"
                        value={numberOfUnits}
                        onChange={(e) => {
                          const updated = responseDetails.products_array.map((pa) =>
                            pa.product_id === p.id
                              ? { ...pa, number_of_units: e.target.value }
                              : pa
                          );

                          setResponseDetails((prev) => ({
                            ...prev,
                            products_array: updated,
                          }));
                        }}
                      />
                    </Stack>
                  )}

                  {isAdded && (
                    <Typography color="green" variant="caption">
                      Added
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>{snackbarMessage}</Alert>
      </Snackbar>
    </Container>
  );
}
